import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import FanChart, { getFanKey, type RatingLevel, type FanRatings, type FanQuadrant } from './FanChart';
import { useFetchUser } from '../../hooks/user-hooks';
import {
  useFetchUserProductExpertise,
  useCreateUserProductExpertiseMutation,
  useUpdateUserProductExpertiseMutation,
  useDeleteUserProductExpertiseMutation,
} from '../../hooks/user-product-expertise-hooks';
import type { RootState } from '../../support/store';

/* ================================================================== */
/*  DOMAIN FAN DATA — Simplified self-rating per sub-domain            */
/* ================================================================== */

/* Inline SVG icons — small, clean, no emoji */
const IconProduct = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
const IconSales = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>;
const IconHandsOn = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
const IconCompete = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;

const DOMAINS: FanQuadrant[] = [
  {
    id: 'product', title: 'Product Knowledge', color: '#3070e7', icon: IconProduct,
    groups: [
      { id: 'pk-main', label: 'Product Knowledge', items: ['Portfolio Overview / Bill of Materials', 'Detailed Domain Knowledge', 'Advanced Solution Knowledge', 'Wider Portfolio & More'] },
    ],
  },
  {
    id: 'sales', title: 'Sales Acumen', color: '#16bdeb', icon: IconSales,
    groups: [
      { id: 'sa-main', label: 'Sales Acumen', items: ['Shadowing & Introductions (Mentee)', 'Sales Tools, Processes & Packaging', 'Sales Process Mastery', 'Wider Business & Mentoring'] },
    ],
  },
  {
    id: 'handson', title: 'Hands-On Experience', color: '#3070e7', icon: IconHandsOn,
    groups: [
      { id: 'ho-hmf', label: 'HMF Domain', items: ['Firewall', 'Multicloud Defense', 'Secure Workload', 'Hypershield', 'Security Cloud Control', 'ISE', 'AI Defense'] },
      { id: 'ho-zta', label: 'ZTA Domain', items: ['DUO/CII', 'Umbrella', 'Secure Access', 'Secure Client'] },
      { id: 'ho-soc', label: 'SOC Domain', items: ['XDR', 'Secure Network Analytics', 'Secure Endpoint', 'Secure Malware Analytics', 'Secure Email', 'Cyber Vision'] },
    ],
  },
  {
    id: 'compete', title: 'Competitive Knowledge', color: '#ff9000', icon: IconCompete,
    groups: [
      { id: 'ck-main', label: 'Competitive Knowledge', items: ['Hybrid Mesh Firewall Competitors', 'Zero Trust Access Competitors', 'SOC Competitors', 'Wider Security, Cloud, AI & More'] },
    ],
  },
];

const DR_KEY = 'stb_domain_ratings';

const loadDomainRatings = (): FanRatings => {
  try { return JSON.parse(localStorage.getItem(DR_KEY) || '{}'); } catch { return {}; }
};
const saveDomainRatings = (m: FanRatings) => {
  localStorage.setItem(DR_KEY, JSON.stringify(m));
};

/* Map expertise level (100/200/300) → RatingLevel (1/2/3) */
const levelToRating = (level: number): RatingLevel =>
  level === 100 ? 1 : level === 200 ? 2 : level === 300 ? 3 : 0;

/* Map RatingLevel (1/2/3) → expertise_level_id (1/2/3) — they happen to match */
const ratingToExpertiseLevelId = (rating: RatingLevel): number => rating as number;

/* Static map: DOMAINS item label → Cisco product ID in the DB */
const ITEM_PRODUCT_ID_MAP: Record<string, number> = {
  // HMF Domain
  'Firewall':                  2,   // Cisco Secure Firewall
  'Multicloud Defense':       70,   // Cisco Multicloud Defense
  'Secure Workload':          16,   // Cisco Secure Workload
  'Hypershield':              79,   // Cisco HyperShield
  'Security Cloud Control':  165,   // Cisco Security Cloud Control
  'ISE':                       1,   // Cisco Identity Services Engine
  'AI Defense':              201,   // Cisco AI Defense
  // ZTA Domain
  'DUO/CII':                  10,   // Cisco Duo
  'Umbrella':                  6,   // Cisco Umbrella
  'Secure Access':            18,   // Cisco Secure Access
  'Secure Client':            29,   // Cisco Secure Client
  // SOC Domain
  'XDR':                      35,   // Cisco XDR
  'Secure Network Analytics': 13,   // Cisco Secure Network Analytics
  'Secure Endpoint':           3,   // Cisco Secure Endpoint
  'Secure Malware Analytics': 12,   // Cisco Secure Malware Analytics
  'Secure Email':             17,   // Cisco Secure Email Threat Defense
  'Cyber Vision':             25,   // Cisco Cyber Vision
};

/* Pre-computed static key list (DOMAINS is a module-level constant) */
const ALL_FAN_KEYS = DOMAINS.flatMap(d => d.groups.flatMap(g => g.items.map(item => getFanKey(d.id, item))));
const TOTAL_ITEMS = ALL_FAN_KEYS.length;

const GROUP_COLORS: Record<string, string[]> = {
  handson: ['#3070e7', '#16bdeb', '#ff9000'],
};

const LEVEL_DESCRIPTIONS = [
  {
    heading: 'L100 – Foundation',
    bullets: [
      'Cisco portfolio & solution knowledge',
      'Customer discovery & buying personas',
      'Product pitch, demo & handling objections',
      'BoM, licensing & lifecycle using CX Services',
    ],
  },
  {
    heading: 'L200 – Advanced',
    bullets: [
      'Solution design & Configuration Knowledge',
      'Scripted demos & product tours',
      'Whiteboarding & competitor depth',
      'Lead workshops & design clinics',
    ],
  },
  {
    heading: 'L300 – Expert',
    bullets: [
      'Custom demos & POV/POC lead',
      'Advanced config & troubleshooting',
      'Multi-architecture solution design',
      'Competitive strategy & key wins',
    ],
  },
];

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */

const SelfEvaluation: React.FC<{ defaultQuadrant?: string }> = ({ defaultQuadrant }) => {
  const email = useSelector((state: RootState) => state.user.userData.email);
  const { data: currentUser } = useFetchUser();
  const userId = currentUser?.id;

  const { data: expertiseRecords } = useFetchUserProductExpertise(userId);

  /* existing DB records keyed by product id */
  const expertiseByProductId = useMemo(() => {
    const map = new Map<number, { recordId: number; level: RatingLevel }>();
    expertiseRecords?.forEach((r) => {
      map.set(r.product.id, { recordId: r.id, level: levelToRating(r.expertise.level) });
    });
    return map;
  }, [expertiseRecords]);

  /* Build FanRatings: DB records merged over localStorage */
  const [domainRatings, setDomainRatings] = useState<FanRatings>(loadDomainRatings);

  /* Ref so handleRate always reads the latest ratings without being recreated on every change */
  const domainRatingsRef = useRef<FanRatings>(domainRatings);
  useEffect(() => { domainRatingsRef.current = domainRatings; }, [domainRatings]);

  useEffect(() => {
    if (!expertiseRecords) return;

    const recordsByProductId = new Map<number, RatingLevel>();
    expertiseRecords.forEach((r) => {
      recordsByProductId.set(r.product.id, levelToRating(r.expertise.level));
    });

    setDomainRatings((prev) => {
      const merged = { ...prev };
      DOMAINS.forEach((domain) => {
        domain.groups.forEach((group) => {
          group.items.forEach((item) => {
            const productId = ITEM_PRODUCT_ID_MAP[item];
            if (productId !== undefined) {
              const key = getFanKey(domain.id, item);
              merged[key] = recordsByProductId.get(productId) ?? 0;
            }
          });
        });
      });
      saveDomainRatings(merged);
      return merged;
    });
  }, [expertiseRecords]);

  const createMutation = useCreateUserProductExpertiseMutation();
  const updateMutation = useUpdateUserProductExpertiseMutation();
  const deleteMutation = useDeleteUserProductExpertiseMutation();

  const handleRate = useCallback((key: string, level?: RatingLevel) => {
    const currentRatings = domainRatingsRef.current;
    const next: RatingLevel =
      level !== undefined ? level : (((currentRatings[key] || 0) + 1) % 4) as RatingLevel;

    const updated = { ...currentRatings, [key]: next };
    saveDomainRatings(updated);
    setDomainRatings(updated);

    if (userId && email) {
      const itemLabel = key.split(':').slice(1).join(':');
      const productId = ITEM_PRODUCT_ID_MAP[itemLabel];
      if (productId !== undefined) {
        const existing = expertiseByProductId.get(productId);
        if (next === 0) {
          if (existing) {
            deleteMutation.mutate({ id: existing.recordId } as any);
          }
        } else if (existing) {
          updateMutation.mutate({
            id: existing.recordId,
            user_id: userId,
            product_id: productId,
            expertise_level_id: ratingToExpertiseLevelId(next),
            updated_by: email,
          } as any);
        } else {
          createMutation.mutate({
            user_id: userId,
            product_id: productId,
            expertise_level_id: ratingToExpertiseLevelId(next),
            updated_by: email,
          } as any);
        }
      }
    }
  }, [userId, email, expertiseByProductId, createMutation, updateMutation, deleteMutation]);

  // Overall progress based on domain ratings
  const ratedItems = ALL_FAN_KEYS.filter(key => (domainRatings[key] || 0) > 0).length;
  const avgRating = TOTAL_ITEMS > 0
    ? ALL_FAN_KEYS.reduce((sum, key) => sum + (domainRatings[key] || 0), 0) / TOTAL_ITEMS
    : 0;
  const overallPct = Math.round((avgRating / 3) * 100); // 3 is max rating

  return (
    <div className="se">
      {!defaultQuadrant && (
        <div className="se__top">
          <div className="se__top-left">
            <span className="se__overall-pct" style={{ background: 'linear-gradient(90deg, #3070e7, #16bdeb 30%, #ff007f 75%, #ff9000)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {overallPct}%
            </span>
            <span className="se__overall-label">Overall Progress · {ratedItems}/{TOTAL_ITEMS} skills rated</span>
          </div>
        </div>
      )}

      <FanChart
        quadrants={DOMAINS}
        ratings={domainRatings}
        onRate={handleRate}
        defaultQuadrant={defaultQuadrant}
        groupColors={GROUP_COLORS}
        levelDescriptions={LEVEL_DESCRIPTIONS}
      />
    </div>
  );
};

export default SelfEvaluation;
